
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

import { AppDispatch, RootState }       from "../../redux/store";
import { addToCart, decrementCartItem, removeFromCart } from "../../redux/cartSlice";
import { listenToProductData }           from "../../redux/productSlice";
import { calculateClassifiedEarnings }   from "../../redux/classifiedCommerceServiceFeeSlice";

import logo        from "../../assets/app/logo.png";
import checkoutPay from "../../assets/payment/checkoutpay.png";
import dhlLogo     from "../../assets/delivery/dhl.png";
import fbaLogo     from "../../assets/delivery/fba.png";
import fbsLogo     from "../../assets/delivery/fbs.png";
import safariLogo  from "../../assets/delivery/xafarii.png";

import {
  ArrowLeft, ShoppingBag, Trash2, ShieldCheck, Heart,
  CreditCard, RotateCcw, Package, Truck,
  Lock, Plus, Minus, AlertCircle, X, Check, Gift,
  Mail, Home as HomeIcon, ChevronDown, Users, ChevronRight, Smartphone,
} from "lucide-react";
import { electronicsSizeAttributes, electronicsColorAttributes } from "../../UNIVERSAL/ATTRIBUTES/ElectronicsAttributes";
import countries, { CountryInfo } from "../../GLOBAL/Country";

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════════════════ */
const T = {
  bg:        "#FFFFFF",
  bgSoft:    "#FAF9FF",
  bgMuted:   "#F0EDF8",
  ink:       "#0A0A0A",
  inkMid:    "#1C1C1C",
  muted:     "#8B82A8",
  mutedSoft: "#C4BBEE",
  border:    "rgba(106,0,255,0.15)",
  borderDark:"rgba(106,0,255,0.35)",
  blue:      "#007BFF",
  purple:    "#6A00FF",
  pink:      "#FF00D4",
  gold:      "#FFD60A",
  red:       "#CC2200",
  green:     "#15803D",
  mpesa:     "#00A651",
  grad:      "linear-gradient(90deg,#007BFF 0%,#6A00FF 50%,#FF00D4 100%)",
};

const GRAD = T.grad;

/* ── API endpoints ── */
const STRIPE_CHECKOUT_URL   = process.env.REACT_APP_META_CHECKOUT_URL         ?? "https://europe-west3-metarelm.cloudfunctions.net/createCheckoutSession";
const COSMOOLA_CHECKOUT_URL = process.env.REACT_APP_COSMOOLA_CHECKOUT_URL     ?? "https://europe-west3-metarelm.cloudfunctions.net/createCosmoolaCheckoutSession";
const DHL_RATES_URL         = process.env.REACT_APP_DHL_RATES_URL             ?? "https://europe-west3-metarelm.cloudfunctions.net/getDhlRates";
const XAFARII_RATES_URL     = process.env.REACT_APP_XAFARII_RATES_URL         ?? "https://europe-west3-metarelm.cloudfunctions.net/getXafariiRates";
const CREATE_CUSTOMER_URL   = process.env.REACT_APP_CREATE_CUSTOMER_URL       ?? "https://europe-west3-metarelm.cloudfunctions.net/createCustomerAccount";

type CheckoutResponse       = { url?: string; data?: { url?: string }; sessionUrl?: string; checkoutUrl?: string };
type CreateCustomerResponse = { userId: string };
type CarrierKey  = "dhl" | "xafarii" | "fba" | "fbs";
type PaymentMethod = "stripe" | "mpesa";

/* ── FBA fee tiers ── */
const calculateFbaFee = (subtotal: number): number => {
  let fee: number;
  if      (subtotal > 5000) fee = subtotal * 0.04;
  else if (subtotal > 2000) fee = subtotal * 0.055;
  else if (subtotal > 1000) fee = subtotal * 0.07;
  else if (subtotal > 500)  fee = subtotal * 0.09;
  else if (subtotal > 200)  fee = subtotal * 0.11;
  else                      fee = subtotal * 0.13;
  return parseFloat(fee.toFixed(2));
};

const FBA_TIERS = [
  { label: "Orders > €5,000",  pct: "4%",   min: 5000 },
  { label: "Orders > €2,000",  pct: "5.5%", min: 2000 },
  { label: "Orders > €1,000",  pct: "7%",   min: 1000 },
  { label: "Orders > €500",    pct: "9%",   min: 500  },
  { label: "Orders > €200",    pct: "11%",  min: 200  },
  { label: "All other orders", pct: "13%",  min: 0    },
];

const fmtPrice   = (n: number, sym = "€") => `${sym}${Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const getSizeName  = (id?: number) => id ? electronicsSizeAttributes.values.find((s: any) => s.id === id)?.name  ?? null : null;
const getColorName = (id?: number) => id ? electronicsColorAttributes.values.find((c: any) => c.id === id)?.name ?? null : null;
const getColorHex  = (id?: number) => id ? electronicsColorAttributes.values.find((c: any) => c.id === id)?.hexCodes?.[0] ?? null : null;

function stripDialCode(raw: string, dialCode: string): string {
  const trimmed = raw.trim();
  if (trimmed.startsWith(dialCode)) return trimmed.slice(dialCode.length).trim();
  for (const c of countries) {
    if (trimmed.startsWith(c.phoneCode)) return trimmed.slice(c.phoneCode.length).trim();
  }
  return trimmed.replace(/^\+/, "");
}

function flagSphere(emoji: string) {
  return (
    <div style={{ position:"relative", width:22, height:22, flexShrink:0 }}>
      <div style={{ position:"absolute", inset:0, borderRadius:"50%", background:"radial-gradient(circle at 35% 32%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.10) 28%, transparent 52%), radial-gradient(circle at 68% 72%, rgba(0,0,0,0.18) 0%, transparent 55%)", zIndex:1, pointerEvents:"none" }} />
      <div style={{ width:22, height:22, borderRadius:"50%", overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, lineHeight:1, background:T.bgMuted, boxShadow:"inset -2px -2px 5px rgba(0,0,0,0.10)", userSelect:"none" }}>{emoji}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   CSS
═══════════════════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  button,select,input,textarea{font-family:inherit;cursor:pointer;}
  ::-webkit-scrollbar{display:none;}
  @keyframes pcSpin{to{transform:rotate(360deg);}}
  @keyframes gradSpin{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
  @keyframes slideUp{from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);}}
  @keyframes mpesaPulse{0%,100%{box-shadow:0 0 0 0 rgba(0,166,81,0.35);}50%{box-shadow:0 0 0 6px rgba(0,166,81,0);}}

  .ci{border:1.5px solid rgba(106,0,255,.15);background:#FFFFFF;border-radius:16px;overflow:hidden;margin-bottom:12px;transition:box-shadow .2s,border-color .2s;}
  .ci:hover{border-color:rgba(106,0,255,.4);box-shadow:0 4px 20px rgba(106,0,255,.10);}

  .ib{width:30px;height:30px;border:1.5px solid rgba(106,0,255,.2);background:transparent;border-radius:8px;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .15s;}
  .ib:hover{background:rgba(106,0,255,.07);border-color:rgba(106,0,255,.45);}
  .ib.danger:hover{border-color:#CC2200!important;background:rgba(204,34,0,.07)!important;}

  /* ── Payment method selector cards ── */
  .pay-method-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:14px;}
  .pay-method-card{
    border:2px solid rgba(106,0,255,.18);background:#fff;border-radius:12px;
    padding:14px 12px;cursor:pointer;display:flex;flex-direction:column;align-items:center;gap:7px;
    transition:all .2s;position:relative;overflow:hidden;user-select:none;
  }
  .pay-method-card:hover{border-color:rgba(106,0,255,.45);background:#FAF9FF;transform:translateY(-2px);}
  .pay-method-card.stripe.selected{border-color:#6A00FF;background:#FAF9FF;box-shadow:0 6px 24px rgba(106,0,255,.2);transform:translateY(-2px);}
  .pay-method-card.mpesa.selected{border-color:#00A651;background:rgba(0,166,81,.04);box-shadow:0 6px 24px rgba(0,166,81,.2);transform:translateY(-2px);}
  .pay-method-top-bar{position:absolute;top:0;left:0;right:0;height:3px;opacity:0;transition:opacity .18s;}
  .pay-method-card.selected .pay-method-top-bar{opacity:1;}
  .pay-method-check{position:absolute;top:7px;right:7px;width:16px;height:16px;border-radius:50%;border:1.5px solid rgba(106,0,255,.2);background:#fff;display:flex;align-items:center;justify-content:center;transition:all .15s;}
  .pay-method-card.stripe.selected .pay-method-check{background:#6A00FF;border-color:#6A00FF;}
  .pay-method-card.mpesa.selected  .pay-method-check{background:#00A651;border-color:#00A651;}
  .pay-method-label{font-size:10px;font-weight:800;letter-spacing:.1em;text-transform:uppercase;color:#0A0A0A;text-align:center;}
  .pay-method-sub{font-size:9px;color:#8B82A8;text-align:center;line-height:1.4;}

  /* ── Pay buttons ── */
  .pay-btn-stripe{
    width:100%;padding:15px 0;border:none;border-radius:12px;
    font-size:11px;font-weight:800;letter-spacing:.18em;font-family:inherit;
    display:flex;align-items:center;justify-content:center;gap:10px;
    transition:all .2s;
  }
  .pay-btn-stripe.active{
    background:linear-gradient(90deg,#007BFF,#6A00FF,#FF00D4);
    background-size:200% 100%;animation:gradSpin 3s ease infinite;
    color:#fff;cursor:pointer;box-shadow:0 10px 32px rgba(106,0,255,.35);
  }
  .pay-btn-stripe.active:hover{transform:translateY(-1px);box-shadow:0 14px 40px rgba(106,0,255,.50);}
  .pay-btn-stripe.inactive{background:rgba(106,0,255,.07);color:#C4BBEE;cursor:not-allowed;}

  .pay-btn-mpesa{
    width:100%;padding:15px 0;border:none;border-radius:12px;
    font-size:11px;font-weight:800;letter-spacing:.18em;font-family:inherit;
    display:flex;align-items:center;justify-content:center;gap:10px;
    transition:all .2s;
  }
  .pay-btn-mpesa.active{
    background:#00A651;color:#fff;cursor:pointer;
    box-shadow:0 10px 32px rgba(0,166,81,.30);
    animation:mpesaPulse 2.5s ease infinite;
  }
  .pay-btn-mpesa.active:hover{background:#009348;transform:translateY(-1px);box-shadow:0 14px 40px rgba(0,166,81,.45);}
  .pay-btn-mpesa.inactive{background:rgba(0,166,81,.08);color:rgba(0,166,81,.4);cursor:not-allowed;}

  .step-card{background:#fff;border:1.5px solid rgba(106,0,255,.15);border-radius:16px;margin-bottom:14px;overflow:hidden;animation:slideUp .28s ease both;}
  .step-card-header{display:flex;align-items:center;gap:12px;padding:16px 18px;border-bottom:1.5px solid rgba(106,0,255,.10);cursor:pointer;user-select:none;transition:background .14s;}
  .step-card-header:hover{background:#FAF9FF;}
  .step-card-body{padding:20px 18px;}

  .fi{width:100%;padding:11px 12px;border:1.5px solid rgba(106,0,255,.18);background:#fff;font-family:inherit;font-size:13px;color:#0A0A0A;outline:none;transition:border-color .15s;border-radius:8px;}
  .fi:focus{border-color:rgba(106,0,255,.55);}
  .fi::placeholder{color:#C4BBEE;}
  .fl{display:block;font-family:inherit;font-size:10px;font-weight:700;color:#8B82A8;letter-spacing:0.12em;text-transform:uppercase;margin-bottom:5px;}

  .step-num{width:24px;height:24px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:inherit;font-size:11px;font-weight:700;flex-shrink:0;}
  .step-num.done{background:linear-gradient(135deg,#6A00FF,#FF00D4);color:#fff;}
  .step-num.active{background:linear-gradient(135deg,#007BFF,#6A00FF);color:#fff;}
  .step-num.pending{background:rgba(106,0,255,.10);color:#8B82A8;}

  .del-opt{border:1.5px solid rgba(106,0,255,.15);padding:14px 16px;cursor:pointer;display:flex;align-items:center;gap:14px;transition:border-color .15s,background .15s;background:#fff;border-radius:12px;margin-bottom:10px;}
  .del-opt:hover{border-color:rgba(106,0,255,.45);background:#FAF9FF;}
  .del-opt.active{border-color:#6A00FF;background:#FAF9FF;}

  .shein-check{width:17px;height:17px;border:1.5px solid rgba(106,0,255,.3);background:#fff;border-radius:4px;display:flex;align-items:center;justify-content:center;flex-shrink:0;cursor:pointer;transition:all .15s;}
  .shein-check.on{background:linear-gradient(135deg,#6A00FF,#FF00D4);border-color:transparent;}

  .bfs-toggle{display:flex;align-items:center;gap:10px;padding:12px 14px;border:1.5px dashed rgba(106,0,255,.3);cursor:pointer;transition:all .15s;background:transparent;width:100%;border-radius:10px;}
  .bfs-toggle:hover{border-color:rgba(106,0,255,.6);}
  .bfs-toggle.on{border-style:solid;border-color:#6A00FF;background:#FAF9FF;}

  .country-dropdown{position:absolute;top:100%;left:0;right:0;z-index:500;background:#fff;border:1.5px solid #6A00FF;border-top:none;max-height:220px;overflow-y:auto;box-shadow:0 8px 24px rgba(106,0,255,.15);border-radius:0 0 10px 10px;}
  .country-option{display:flex;align-items:center;gap:10px;padding:9px 12px;cursor:pointer;font-family:inherit;font-size:12px;color:#0A0A0A;transition:background .12s;border-bottom:1px solid rgba(106,0,255,.08);}
  .country-option:hover{background:#FAF9FF;}
  .dial-dropdown{position:absolute;top:100%;left:0;z-index:500;background:#fff;border:1.5px solid #6A00FF;border-top:none;width:220px;max-height:220px;overflow-y:auto;box-shadow:0 8px 24px rgba(106,0,255,.15);border-radius:0 0 10px 10px;}
  .dial-option{display:flex;align-items:center;gap:10px;padding:9px 12px;cursor:pointer;font-family:inherit;font-size:12px;color:#0A0A0A;transition:background .12s;border-bottom:1px solid rgba(106,0,255,.08);}
  .dial-option:hover{background:#FAF9FF;}

  .bc-step{font-size:10px;font-weight:600;color:#C4BBEE;letter-spacing:0.12em;font-family:inherit;text-transform:uppercase;}
  .bc-step.active{color:#6A00FF;font-weight:800;}
  .bc-sep{color:#C4BBEE;font-size:10px;margin:0 7px;}

  /* ── Fulfillment cards ── */
  .fulf-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:22px;}
  @media(max-width:580px){.fulf-grid{grid-template-columns:repeat(2,1fr);}}

  /* ── Rate rows ── */
  .rate-row{border:1.5px solid rgba(106,0,255,.15);padding:12px 14px;cursor:pointer;display:flex;align-items:center;gap:12px;transition:border-color .15s,background .15s;background:#fff;border-radius:10px;margin-bottom:8px;}
  .rate-row:hover{border-color:rgba(106,0,255,.45);background:#FAF9FF;}
  .rate-row.active{border-color:#6A00FF;background:#FAF9FF;}

  /* ── FBA tier table ── */
  .fba-tier-row{display:flex;justify-content:space-between;align-items:center;padding:8px 12px;font-size:11px;border-bottom:1px solid rgba(106,0,255,.07);}
  .fba-tier-row:last-child{border-bottom:none;}
  .fba-tier-row.hi{background:rgba(21,128,61,.06);}
`;

/* ── Fee Row ── */
const FeeRow: React.FC<{
  label:string; value:string; sub?:string;
  bold?:boolean; muted?:boolean; green?:boolean; red?:boolean; loading?:boolean;
}> = ({ label, value, sub, bold, muted, green, red, loading }) => (
  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:`1px solid ${T.border}` }}>
    <div>
      <div style={{ fontSize:bold?14:12, fontWeight:bold?700:500, color:muted?T.muted:T.ink }}>{label}</div>
      {sub && <div style={{ fontSize:10, color:T.muted, marginTop:2 }}>{sub}</div>}
    </div>
    {loading
      ? <span style={{ fontSize:11, color:T.muted }}>Calculating…</span>
      : <span style={{ fontSize:bold?22:13, fontWeight:bold?900:600, color:red?T.red:green?T.green:T.ink }}>{value}</span>
    }
  </div>
);

/* ── Step Badge ── */
const StepBadge: React.FC<{ n:number; current:number }> = React.memo(({ n, current }) => (
  <div className={`step-num${n<current?" done":n===current?" active":" pending"}`}>
    {n<current?<Check size={12} color="#fff"/>:n}
  </div>
));

/* ── Field wrapper ── */
const Field: React.FC<{ label:string; children:React.ReactNode }> = ({ label, children }) => (
  <div><label className="fl">{label}</label>{children}</div>
);

/* ── Cart Item Card ── */
const CartItemCard: React.FC<{
  item:any; listing:any; index:number; sym:string;
  onInc:()=>void; onDec:()=>void; onRemove:()=>void; onNavigate:()=>void;
}> = React.memo(({ item, listing, index, sym, onInc, onDec, onRemove, onNavigate }) => {
  const [removing, setRemoving] = useState(false);
  const [wished,   setWished]   = useState(false);
  const colorName = getColorName(item.colorId);
  const colorHex  = getColorHex(item.colorId);
  const sizeName  = getSizeName(item.sizeId);
  const previewImg = item.colorId && listing?.variants?.[item.colorId]?.imageUrl
    ? listing.variants[item.colorId].imageUrl
    : listing?.photos?.[0] ?? item.photo;
  const handleRemove = () => { setRemoving(true); setTimeout(onRemove, 300); };
  return (
    <motion.div className="ci"
      initial={{ opacity:0, y:14 }}
      animate={{ opacity:removing?0:1, y:removing?-8:0, scale:removing?.97:1 }}
      transition={{ duration:.3, delay:removing?0:index*.06 }}
    >
      <div style={{ display:"flex" }}>
        <div onClick={onNavigate} style={{ width:96, flexShrink:0, background:T.bgMuted, cursor:"pointer", position:"relative", overflow:"hidden", borderRadius:"14px 0 0 14px" }}>
          {previewImg
            ? <img src={previewImg} alt={item.name} style={{ width:"100%", height:"100%", objectFit:"cover", minHeight:120, transition:"transform .4s" }}
                onMouseEnter={e=>(e.currentTarget.style.transform="scale(1.06)")}
                onMouseLeave={e=>(e.currentTarget.style.transform="scale(1)")} />
            : <div style={{ width:"100%", minHeight:120, display:"flex", alignItems:"center", justifyContent:"center" }}><Package size={24} color={T.mutedSoft}/></div>
          }
        </div>
        <div style={{ flex:1, padding:"14px 16px", display:"flex", flexDirection:"column", gap:7 }}>
          <div style={{ fontSize:14, fontWeight:700, color:T.ink, lineHeight:1.3, cursor:"pointer" }} onClick={onNavigate}>{item.name}</div>
          {(colorName || sizeName) && (
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              {colorHex && <div style={{ width:12, height:12, borderRadius:"50%", background:colorHex, border:`1px solid ${T.border}`, flexShrink:0 }} />}
              <span style={{ fontSize:10, color:T.muted, letterSpacing:".08em" }}>{[colorName,sizeName].filter(Boolean).join(" · ")}</span>
            </div>
          )}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:4 }}>
            <div style={{ display:"flex", alignItems:"center", border:`1.5px solid rgba(106,0,255,.2)`, borderRadius:999, overflow:"hidden" }}>
              <button onClick={onDec} style={{ width:30, height:30, border:"none", background:"transparent", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:T.purple, borderRight:`1px solid rgba(106,0,255,.15)` }}><Minus size={11}/></button>
              <div style={{ width:34, height:30, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:800, color:T.ink }}>{item.qty}</div>
              <button onClick={onInc} style={{ width:30, height:30, border:"none", background:"transparent", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:T.purple, borderLeft:`1px solid rgba(106,0,255,.15)` }}><Plus size={11}/></button>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:15, fontWeight:900, color:T.ink }}>{fmtPrice(item.qty*item.price,sym)}</div>
                <div style={{ fontSize:10, color:T.muted }}>{fmtPrice(item.price,sym)} each</div>
              </div>
              <button className="ib" onClick={()=>setWished(v=>!v)}><Heart size={12} color={wished?T.red:T.muted} fill={wished?T.red:"none"}/></button>
              <button className="ib danger" onClick={handleRemove}><Trash2 size={12} color={T.red}/></button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
});

/* ── Country Picker ── */
const CountryField: React.FC<{ value:string; onChange:(c:CountryInfo)=>void }> = React.memo(({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = React.useRef<HTMLDivElement>(null);
  const selected = countries.find(c => c.name === value);
  const filtered = useMemo(() => { const q = query.toLowerCase(); return q ? countries.filter(c => c.name.toLowerCase().includes(q) || c.phoneCode.includes(q)) : countries; }, [query]);
  useEffect(() => { const fn=(e:MouseEvent)=>{ if(ref.current&&!ref.current.contains(e.target as Node))setOpen(false); }; document.addEventListener("mousedown",fn); return()=>document.removeEventListener("mousedown",fn); },[]);
  return (
    <div ref={ref} style={{ position:"relative" }}>
      <div onMouseDown={e => { e.preventDefault(); setOpen(o=>!o); setQuery(""); }}
        style={{ display:"flex", alignItems:"center", gap:9, padding:"10px 12px", border:`1.5px solid ${open?"#6A00FF":"rgba(106,0,255,.18)"}`, background:"#fff", cursor:"pointer", borderRadius:8 }}>
        {selected?<>{flagSphere(selected.emoji)}<span style={{ fontSize:13, color:T.ink, flex:1 }}>{selected.name}</span></>:<span style={{ fontSize:13, color:T.mutedSoft, flex:1 }}>Select country</span>}
        <ChevronDown size={13} color={T.muted} style={{ transition:"transform .15s", transform:open?"rotate(180deg)":"none" }}/>
      </div>
      <AnimatePresence>
        {open&&(
          <motion.div className="country-dropdown" onMouseDown={e=>e.preventDefault()} initial={{opacity:0,y:-4}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-4}} transition={{duration:.14}}>
            <div style={{ padding:"8px 10px", borderBottom:`1px solid rgba(106,0,255,.08)`, position:"sticky", top:0, background:"#fff" }}>
              <input autoFocus placeholder="Search country…" value={query} onChange={e=>setQuery(e.target.value)} style={{ width:"100%", padding:"7px 10px", border:`1.5px solid rgba(106,0,255,.18)`, borderRadius:6, fontFamily:"inherit", fontSize:12, outline:"none", color:T.ink }}/>
            </div>
            {filtered.slice(0,80).map(c=>(
              <div key={c.code} className="country-option" onClick={()=>{onChange(c);setOpen(false);setQuery("");}}>
                {flagSphere(c.emoji)}<span style={{flex:1}}>{c.name}</span><span style={{color:T.muted,fontSize:11}}>{c.phoneCode}</span>
              </div>
            ))}
            {filtered.length===0&&<div style={{padding:"14px 12px",fontSize:12,color:T.muted}}>No results</div>}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

/* ── Dial Picker ── */
const DialField: React.FC<{ dialCode:string; onChange:(code:string)=>void }> = React.memo(({ dialCode, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = React.useRef<HTMLDivElement>(null);
  const selected = countries.find(c => c.phoneCode === dialCode);
  const filtered = useMemo(()=>{ const q=query.toLowerCase(); return q?countries.filter(c=>c.name.toLowerCase().includes(q)||c.phoneCode.includes(q)):countries; },[query]);
  useEffect(()=>{ const fn=(e:MouseEvent)=>{ if(ref.current&&!ref.current.contains(e.target as Node))setOpen(false); }; document.addEventListener("mousedown",fn); return()=>document.removeEventListener("mousedown",fn); },[]);
  return (
    <div ref={ref} style={{ position:"relative", flexShrink:0 }}>
      <div onMouseDown={e => { e.preventDefault(); setOpen(o=>!o); setQuery(""); }}
        style={{ display:"flex", alignItems:"center", gap:7, padding:"11px 10px", border:`1.5px solid rgba(106,0,255,.18)`, borderRight:"none", background:T.bgSoft, cursor:"pointer", minWidth:90, borderRadius:"8px 0 0 8px" }}>
        {selected&&flagSphere(selected.emoji)}
        <span style={{fontSize:12,color:T.ink}}>{dialCode}</span>
        <ChevronDown size={11} color={T.muted}/>
      </div>
      <AnimatePresence>
        {open&&(
          <motion.div className="dial-dropdown" onMouseDown={e=>e.preventDefault()} initial={{opacity:0,y:-4}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-4}} transition={{duration:.14}}>
            <div style={{padding:"7px 8px",borderBottom:`1px solid rgba(106,0,255,.08)`,position:"sticky",top:0,background:"#fff"}}>
              <input autoFocus placeholder="Search…" value={query} onChange={e=>setQuery(e.target.value)} style={{width:"100%",padding:"6px 8px",border:`1.5px solid rgba(106,0,255,.18)`,borderRadius:6,fontFamily:"inherit",fontSize:11,outline:"none"}}/>
            </div>
            {filtered.slice(0,80).map(c=>(
              <div key={c.code} className="dial-option" onClick={()=>{onChange(c.phoneCode);setOpen(false);setQuery("");}}>
                {flagSphere(c.emoji)}<span style={{flex:1,fontSize:11}}>{c.name}</span><span style={{color:T.muted,fontSize:11,fontWeight:700}}>{c.phoneCode}</span>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

/* ═══════════════════════════════════════════════
   STEP COMPONENTS
═══════════════════════════════════════════════ */

/* ── Cart Step ── */
interface CartStepProps {
  items: any[]; listings: any[]; stepIdx: number; sym: string;
  onContinue: ()=>void;
  onInc:(item:any)=>void; onDec:(item:any)=>void;
  onRemove:(item:any)=>void; onNavigate:(id:string)=>void;
}
const CartStep: React.FC<CartStepProps> = React.memo(({
  items, listings, stepIdx, sym,
  onContinue, onInc, onDec, onRemove, onNavigate,
}) => {
  const getListing = (id:string) => (listings as any[]).find((l:any)=>l.listingId===id);
  const totalSubtotal = items.reduce((s:number,i:any)=>s+i.qty*i.price,0);
  return (
    <div className="step-card">
      <div className="step-card-header">
        <StepBadge n={1} current={stepIdx+1}/>
        <span style={{fontSize:12,fontWeight:800,letterSpacing:"0.12em",textTransform:"uppercase" as const,color:T.ink}}>Cart Items</span>
        <span style={{marginLeft:"auto",fontSize:11,color:T.muted}}>{items.length} item{items.length!==1?"s":""}</span>
      </div>
      {stepIdx===0?(
        <div className="step-card-body" style={{padding:0}}>
          {totalSubtotal < 300 && (
            <div style={{ display:"flex", alignItems:"center", gap:8, background:"#FAF9FF", borderBottom:`1px solid rgba(106,0,255,.10)`, padding:"9px 16px" }}>
              <Truck size={13} color={T.purple}/>
              <span style={{ fontSize:12, color:T.ink }}>Add <strong style={{color:T.purple}}>{fmtPrice(300-totalSubtotal,sym)}</strong> more for <strong style={{color:T.purple}}>Free Shipping!</strong></span>
            </div>
          )}
          {items.map((item:any,i:number)=>(
            <CartItemCard
              key={`${item.listingId}-${item.colorId??"nc"}-${item.sizeId??"ns"}`}
              item={item} listing={getListing(item.listingId)} index={i} sym={sym}
              onInc={()=>onInc(item)} onDec={()=>onDec(item)}
              onRemove={()=>onRemove(item)} onNavigate={()=>onNavigate(item.listingId)}
            />
          ))}
          <div style={{padding:"14px 16px",borderTop:`1px solid ${T.border}`}}>
            <button onClick={onContinue} className="pay-btn-stripe active">Continue to Delivery <ChevronRight size={14}/></button>
          </div>
        </div>
      ):(
        <div style={{padding:"10px 16px",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap" as const}}>
          {items.slice(0,3).map((item:any)=>(
            <div key={item.listingId} style={{width:40,height:52,background:T.bgMuted,overflow:"hidden",flexShrink:0,borderRadius:6}}>
              {item.photo&&<img src={item.photo} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>}
            </div>
          ))}
          {items.length>3&&<span style={{fontSize:11,color:T.muted}}>+{items.length-3} more</span>}
        </div>
      )}
    </div>
  );
});

/* ── Delivery Step ── */
type AddrState = {
  firstName:string; lastName:string; email:string;
  country:string; state:string; city:string; postalCode:string;
  address:string; dialCode:string; phone:string;
};

interface DeliveryStepProps {
  stepIdx: number; isLoggedIn: boolean; initialAddr: AddrState;
  buyForSomeoneElse: boolean; onToggleBuyForSomeoneElse: () => void;
  onFlush: (patch: Partial<AddrState>) => void;
  onContinue: (latest: AddrState) => void; onBack: () => void;
}
const DeliveryStep: React.FC<DeliveryStepProps> = React.memo(({
  stepIdx, isLoggedIn, initialAddr, buyForSomeoneElse, onToggleBuyForSomeoneElse,
  onFlush, onContinue, onBack,
}) => {
  const [country,  setCountry]  = useState(initialAddr.country);
  const [dialCode, setDialCode] = useState(initialAddr.dialCode);
  const prevInit = useRef(initialAddr);
  useEffect(() => {
    if (prevInit.current !== initialAddr) {
      prevInit.current = initialAddr;
      setCountry(initialAddr.country);
      setDialCode(initialAddr.dialCode);
    }
  }, [initialAddr]);
  const rFirstName = useRef<HTMLInputElement>(null);
  const rLastName  = useRef<HTMLInputElement>(null);
  const rEmail     = useRef<HTMLInputElement>(null);
  const rState     = useRef<HTMLInputElement>(null);
  const rCity      = useRef<HTMLInputElement>(null);
  const rPostal    = useRef<HTMLInputElement>(null);
  const rAddress   = useRef<HTMLInputElement>(null);
  const rPhone     = useRef<HTMLInputElement>(null);
  const handleCountry = (c: CountryInfo) => { setCountry(c.name); setDialCode(c.phoneCode); onFlush({ country: c.name, dialCode: c.phoneCode }); };
  const handleDial    = (v: string) => { setDialCode(v); onFlush({ dialCode: v }); };
  const blurField = (key: keyof AddrState) => (e: React.FocusEvent<HTMLInputElement>) => { onFlush({ [key]: e.target.value }); };
  const blurPhone = (e: React.FocusEvent<HTMLInputElement>) => {
    const clean = stripDialCode(e.target.value, dialCode);
    if (e.target.value !== clean) e.target.value = clean;
    onFlush({ phone: clean });
  };
  const handleContinue = () => {
    const latest: AddrState = {
      firstName: rFirstName.current?.value ?? "", lastName: rLastName.current?.value ?? "",
      email: rEmail.current?.value ?? "", state: rState.current?.value ?? "",
      city: rCity.current?.value ?? "", postalCode: rPostal.current?.value ?? "",
      address: rAddress.current?.value ?? "",
      phone: stripDialCode(rPhone.current?.value ?? "", dialCode),
      country, dialCode,
    };
    if (rPhone.current && rPhone.current.value !== latest.phone) rPhone.current.value = latest.phone;
    onContinue(latest);
  };
  return (
    <div className="step-card">
      <div className="step-card-header" onClick={stepIdx>1?onBack:undefined}>
        <StepBadge n={2} current={stepIdx+1}/>
        <span style={{fontSize:12,fontWeight:800,letterSpacing:"0.12em",textTransform:"uppercase" as const,color:T.ink}}>Contact & Delivery</span>
        {stepIdx>1&&<Check size={14} color={T.green} style={{marginLeft:"auto"}}/>}
      </div>
      {stepIdx===1?(
        <div className="step-card-body">
          {isLoggedIn&&(
            <div style={{display:"flex",alignItems:"center",gap:8,background:"rgba(21,128,61,0.06)",border:"1px solid rgba(21,128,61,0.2)",padding:"9px 12px",marginBottom:16,borderRadius:8}}>
              <Check size={12} color={T.green}/>
              <p style={{fontSize:11,color:T.green}}>Pre-filled from your profile — all fields are still editable.</p>
            </div>
          )}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
            <Field label="First Name *"><input ref={rFirstName} className="fi" placeholder="e.g. Alex" defaultValue={initialAddr.firstName} onBlur={blurField("firstName")}/></Field>
            <Field label="Last Name *"><input ref={rLastName} className="fi" placeholder="e.g. Smith" defaultValue={initialAddr.lastName} onBlur={blurField("lastName")}/></Field>
          </div>
          <div style={{marginBottom:14}}>
            <Field label="Email Address *">
              <div style={{position:"relative"}}>
                <input ref={rEmail} className="fi" placeholder="your@email.com" defaultValue={initialAddr.email} onBlur={blurField("email")} style={{paddingLeft:38}}/>
                <Mail size={13} color={T.muted} style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)"}}/>
              </div>
            </Field>
          </div>
          <div style={{marginBottom:18}}>
            <button className={`bfs-toggle${buyForSomeoneElse?" on":""}`} onClick={onToggleBuyForSomeoneElse}>
              <div className={`shein-check${buyForSomeoneElse?" on":""}`}>{buyForSomeoneElse&&<Check size={10} color="#fff"/>}</div>
              <Users size={13} color={buyForSomeoneElse?T.purple:T.muted}/>
              <div style={{textAlign:"left" as const}}>
                <p style={{fontSize:11,fontWeight:700,color:buyForSomeoneElse?T.purple:T.muted,letterSpacing:"0.08em"}}>BUY FOR SOMEONE ELSE</p>
                <p style={{fontSize:10,color:T.muted,marginTop:2}}>Ship to a different person — we'll set up an account for them</p>
              </div>
            </button>
            <AnimatePresence>
              {buyForSomeoneElse&&(
                <motion.div initial={{opacity:0,y:-8,height:0}} animate={{opacity:1,y:0,height:"auto"}} exit={{opacity:0,y:-8,height:0}} transition={{duration:.2}}
                  style={{marginTop:10,padding:14,background:T.bgSoft,border:`1.5px solid rgba(106,0,255,.2)`,overflow:"hidden",borderRadius:10}}>
                  <p style={{fontSize:10,color:T.muted,marginBottom:12,letterSpacing:"0.1em"}}>RECIPIENT DETAILS — we'll create an account using this email</p>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                    <Field label="Recipient First Name *"><input className="fi" placeholder="Their first name" defaultValue={initialAddr.firstName} onBlur={blurField("firstName")}/></Field>
                    <Field label="Recipient Last Name *"><input className="fi" placeholder="Their last name" defaultValue={initialAddr.lastName} onBlur={blurField("lastName")}/></Field>
                  </div>
                  <Field label="Recipient Email *">
                    <div style={{position:"relative"}}>
                      <input className="fi" placeholder="recipient@email.com" defaultValue={initialAddr.email} onBlur={blurField("email")} style={{paddingLeft:38}}/>
                      <Mail size={13} color={T.muted} style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)"}}/>
                    </div>
                  </Field>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
            <Field label="Country *"><CountryField value={country} onChange={handleCountry}/></Field>
            <Field label="State / Province"><input ref={rState} className="fi" placeholder="e.g. California" defaultValue={initialAddr.state} onBlur={blurField("state")}/></Field>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
            <Field label="City *"><input ref={rCity} className="fi" placeholder="e.g. New York" defaultValue={initialAddr.city} onBlur={blurField("city")}/></Field>
            <Field label="Postal Code *"><input ref={rPostal} className="fi" placeholder="e.g. 10001" defaultValue={initialAddr.postalCode} onBlur={blurField("postalCode")}/></Field>
          </div>
          <div style={{marginBottom:14}}>
            <Field label="Delivery Address *">
              <div style={{position:"relative"}}>
                <input ref={rAddress} className="fi" placeholder="Street, apartment, building" defaultValue={initialAddr.address} onBlur={blurField("address")} style={{paddingLeft:38}}/>
                <HomeIcon size={13} color={T.muted} style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)"}}/>
              </div>
            </Field>
          </div>
          <div style={{marginBottom:20}}>
            <Field label="WhatsApp Number *">
              <div style={{display:"flex"}}>
                <DialField dialCode={dialCode} onChange={handleDial}/>
                <input ref={rPhone} className="fi" placeholder="e.g. 7001234567"
                  defaultValue={stripDialCode(initialAddr.phone, initialAddr.dialCode)}
                  onBlur={blurPhone} style={{flex:1,borderRadius:"0 8px 8px 0"}} inputMode="tel"/>
              </div>
              <p style={{fontSize:10,color:T.muted,marginTop:5}}>{dialCode} + your number · dial code added automatically</p>
            </Field>
          </div>
          <button onClick={handleContinue} className="pay-btn-stripe active">Continue to Shipping <ChevronRight size={14}/></button>
        </div>
      ):(
        stepIdx>1&&(
          <div style={{padding:"10px 16px",display:"flex",alignItems:"center",gap:8}}>
            {country&&(()=>{const c=countries.find(x=>x.name===country);return c?flagSphere(c.emoji):null;})()}
            <p style={{fontSize:12,color:T.muted}}>
              {initialAddr.firstName} {initialAddr.lastName} · {initialAddr.city}, {country}
              {buyForSomeoneElse&&<span style={{marginLeft:6,fontSize:10,color:T.purple,fontWeight:700,letterSpacing:"0.06em"}}>· GIFT</span>}
            </p>
          </div>
        )
      )}
    </div>
  );
});

/* ═══════════════════════════════════════════════════════════
   STEP 3: SHIPPING — Full FBS/FBP/FBA + Payment Method Selector
═══════════════════════════════════════════════════════════ */
interface ShippingStepProps {
  stepIdx: number; addr: AddrState;
  subtotal: number; totalQty: number; sym: string;
  grandTotal: number;
  canPay: boolean; payingStripe: boolean; payingMpesa: boolean;
  payError: string | null; onDismissError: () => void;
  onPayStripe: () => void; onPayMpesa: () => void;
  selectedPayment: PaymentMethod; onSelectPayment: (p: PaymentMethod) => void;
  /* FBS */
  checkedBrandIds: string[];
  brandDhlRates: Record<string,any[]>; brandDhlLoading: Record<string,boolean>;
  brandDhlErrors: Record<string,string|null>; selectedBrandRates: Record<string,any>;
  onSelectBrandRate: (brandId:string, rate:any)=>void; fetchBrandDhlRates: ()=>void;
  /* fulfillment */
  selectedFulfillment: "FBS"|"FBP"|"FBA"; onSelectFulfillment: (f:"FBS"|"FBP"|"FBA")=>void;
  /* carrier */
  selectedCarrier: CarrierKey; onSelectCarrier: (c:CarrierKey)=>void;
  /* DHL */
  dhlRates: any[]; dhlLoading: boolean; dhlError: string|null;
  selectedDhlRate: any; onSelectDhlRate: (r:any)=>void; fetchDhlRates: ()=>void;
  /* Xafarii */
  safaRates: any[]; safaLoading: boolean; safaError: string|null;
  selectedSafaRate: any; onSelectSafaRate: (r:any)=>void; fetchSafaRates: ()=>void;
}

const ShippingStep: React.FC<ShippingStepProps> = React.memo(({
  stepIdx, addr, subtotal, totalQty, sym,
  grandTotal, canPay, payingStripe, payingMpesa, payError, onDismissError,
  onPayStripe, onPayMpesa, selectedPayment, onSelectPayment,
  checkedBrandIds, brandDhlRates, brandDhlLoading, brandDhlErrors,
  selectedBrandRates, onSelectBrandRate, fetchBrandDhlRates,
  selectedFulfillment, onSelectFulfillment,
  selectedCarrier, onSelectCarrier,
  dhlRates, dhlLoading, dhlError, selectedDhlRate, onSelectDhlRate, fetchDhlRates,
  safaRates, safaLoading, safaError, selectedSafaRate, onSelectSafaRate, fetchSafaRates,
}) => {
  const fbaFee = calculateFbaFee(subtotal);
  const fbsTotal  = checkedBrandIds.reduce((sum,bid)=>{ const r=selectedBrandRates[bid]; return sum+(r?(r.totalPrice??r.price??0):0); },0);
  const dhlTotal  = selectedDhlRate  ? (selectedDhlRate.totalPrice  ?? 0) : 0;
  const safaTotal = selectedSafaRate ? (selectedSafaRate.totalPrice ?? 0) : 0;
  const fbpTotal  = selectedCarrier==="dhl" ? dhlTotal : safaTotal;
  const deliveryFee = selectedFulfillment==="FBS" ? fbsTotal : selectedFulfillment==="FBP" ? fbpTotal : fbaFee;
  const paying = payingStripe || payingMpesa;

  type CarrierDef = { key:CarrierKey; logo:string; label:string; topBar:string; feeColor:string; fee:number|null };
  const CARRIERS: CarrierDef[] = [
    { key:"fbs",     logo:fbsLogo,    label:"FBS · Seller",    topBar:"linear-gradient(90deg,#6A00FF,#FF00D4)", feeColor:T.purple,  fee: checkedBrandIds.some(bid=>selectedBrandRates[bid]) ? fbsTotal : null },
    //{ key:"dhl",     logo:dhlLogo,    label:"DHL Express",     topBar:"linear-gradient(90deg,#FFCC00,#F5A800)", feeColor:"#D4A500", fee: selectedDhlRate  ? dhlTotal  : null },
    //{ key:"xafarii", logo:safariLogo, label:"Xafarii",         topBar:"linear-gradient(90deg,#E05A00,#FF7A00)", feeColor:"#E05A00", fee: selectedSafaRate ? safaTotal : null },
    //{ key:"fba",     logo:fbaLogo,    label:"FBA · Metarelm",  topBar:"linear-gradient(90deg,#15803D,#1a9e4f)", feeColor:T.green,   fee: fbaFee },
  ];

  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="step-card">
      <div className="step-card-header" onClick={()=>setCollapsed(v=>!v)} style={{cursor:"pointer"}}>
        <StepBadge n={3} current={stepIdx+1}/>
        <span style={{fontSize:12,fontWeight:800,letterSpacing:"0.12em",textTransform:"uppercase" as const,color:T.ink}}>Delivery & Payment</span>
        <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:8}}>
          {collapsed&&(
            <span style={{fontSize:11,color:T.muted,fontWeight:600}}>
              {selectedCarrier.toUpperCase()} · {fmtPrice(deliveryFee,sym)}
            </span>
          )}
          <ChevronDown size={14} color={T.muted} style={{transition:"transform .2s",transform:collapsed?"rotate(0deg)":"rotate(180deg)"}}/>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {!collapsed && stepIdx===2 && (
          <motion.div key="ship-body"
            initial={{height:0,opacity:0}} animate={{height:"auto",opacity:1}}
            exit={{height:0,opacity:0}} transition={{duration:.25,ease:[.4,0,.2,1]}}
            style={{overflow:"hidden"}}>
            <div className="step-card-body">

              {/* ── 4 full-bleed carrier image cards ── */}
              <div className="fulf-grid">
                {CARRIERS.map(c=>{
                  const sel = selectedCarrier===c.key;
                  return (
                    <div key={c.key} onClick={()=>onSelectCarrier(c.key)}
                      style={{
                        position:"relative", borderRadius:14, overflow:"hidden", cursor:"pointer", aspectRatio:"3/4",
                        border:`2.5px solid ${sel?c.feeColor:"transparent"}`,
                        boxShadow:sel?`0 8px 28px ${c.feeColor}44`:"0 2px 12px rgba(106,0,255,.10)",
                        transform:sel?"translateY(-4px) scale(1.03)":"translateY(0) scale(1)",
                        transition:"all .22s cubic-bezier(.4,0,.2,1)", userSelect:"none",
                      }}>
                      <img src={c.logo} alt={c.label}
                        style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover", transition:"transform .3s", transform:sel?"scale(1.06)":"scale(1)" }}
                        onError={e=>{ e.currentTarget.style.display="none"; }}/>
                      <div style={{ position:"absolute", inset:0, background:c.topBar, opacity:0 }}/>
                      <div style={{ position:"absolute", inset:0, background:"linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.18) 55%, rgba(0,0,0,0) 100%)", pointerEvents:"none" }}/>
                      <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:c.topBar, opacity:sel?1:0, transition:"opacity .2s" }}/>
                      <div style={{
                        position:"absolute", top:8, right:8, width:20, height:20, borderRadius:"50%",
                        background:sel?c.feeColor:"rgba(255,255,255,0.18)",
                        border:`1.5px solid ${sel?c.feeColor:"rgba(255,255,255,0.4)"}`,
                        display:"flex", alignItems:"center", justifyContent:"center", transition:"all .18s",
                      }}>
                        {sel&&<Check size={10} color={c.key==="dhl"?"#0D0D0D":"#fff"}/>}
                      </div>
                      <div style={{ position:"absolute", bottom:0, left:0, right:0, padding:"10px 10px 12px", display:"flex", flexDirection:"column", gap:3 }}>
                        <span style={{ fontSize:10, fontWeight:900, letterSpacing:".1em", textTransform:"uppercase" as const, color:"#fff", textShadow:"0 1px 4px rgba(0,0,0,0.5)" }}>{c.label}</span>
                        <span style={{ fontSize:13, fontWeight:900, color:sel?c.feeColor:"#fff", textShadow:"0 1px 6px rgba(0,0,0,0.6)" }}>
                          {c.fee!==null ? fmtPrice(c.fee,sym) : <span style={{fontSize:10,opacity:0.6}}>—</span>}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* ── Rate pickers per carrier ── */}
              <AnimatePresence mode="wait">
                {/* FBS */}
                {selectedFulfillment==="FBS" && selectedCarrier==="fbs" && (
                  <motion.div key="fbs-rates" initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-6}} transition={{duration:.22}}>
                    <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14, padding:"12px 14px", background:T.bgSoft, border:`1.5px solid rgba(106,0,255,.15)`, borderRadius:10 }}>
                      <img src={fbsLogo} alt="FBS" style={{height:26,objectFit:"contain"}} onError={e=>{e.currentTarget.style.display="none";}}/>
                      <div><p style={{fontSize:12,fontWeight:700,color:T.ink}}>Fulfilled by Seller</p><p style={{fontSize:11,color:T.muted}}>Each brand ships independently to {addr.city||addr.country||"your address"}</p></div>
                    </div>
                    {checkedBrandIds.map(bid=>{
                      const rates=brandDhlRates[bid]??[]; const loading=brandDhlLoading[bid]??false;
                      const error=brandDhlErrors[bid]??null; const selRate=selectedBrandRates[bid];
                      return (
                        <div key={bid} style={{marginBottom:14,border:`1.5px solid rgba(106,0,255,.12)`,borderRadius:10,overflow:"hidden"}}>
                          <div style={{padding:"8px 13px",background:"#FAF9FF",borderBottom:`1px solid rgba(106,0,255,.08)`,display:"flex",alignItems:"center",gap:8}}>
                            <Truck size={12} color={T.muted}/><span style={{fontSize:11,fontWeight:700,color:T.ink}}>Brand {bid.slice(0,8)}…</span>
                          </div>
                          <div style={{padding:"10px 13px"}}>
                            {loading&&<div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 0"}}><span style={{width:13,height:13,border:`2px solid rgba(106,0,255,.2)`,borderTopColor:T.purple,borderRadius:"50%",animation:"pcSpin 1s linear infinite",display:"inline-block"}}/><span style={{fontSize:11,color:T.muted}}>Fetching rates…</span></div>}
                            {error&&!loading&&<div style={{display:"flex",alignItems:"center",gap:8}}><AlertCircle size={12} color={T.red}/><span style={{fontSize:11,color:T.red,flex:1}}>{error}</span><button onClick={fetchBrandDhlRates} style={{fontSize:10,color:T.purple,background:"none",border:"none",cursor:"pointer",textDecoration:"underline"}}>Retry</button></div>}
                            {!loading&&!error&&rates.length===0&&<div style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0"}}><Truck size={12} color={T.green}/><span style={{fontSize:11,color:T.green}}>Free delivery for this seller</span></div>}
                            {!loading&&rates.map((rate:any,i:number)=>{
                              const isSel=selRate?.productCode===rate.productCode;
                              const price=rate.totalPrice??rate.price??0;
                              const days=rate.deliveryCapabilities?.estimatedDeliveryDateAndTime??rate.deliveryTime??"2–5 days";
                              return (
                                <div key={i} className={`rate-row${isSel?" active":""}`} onClick={()=>onSelectBrandRate(bid,rate)}>
                                  <div style={{width:15,height:15,borderRadius:"50%",border:`2px solid ${isSel?T.purple:"rgba(106,0,255,.2)"}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                                    {isSel&&<div style={{width:6,height:6,borderRadius:"50%",background:T.purple}}/>}
                                  </div>
                                  <img src={fbsLogo} alt="" style={{height:18,objectFit:"contain",flexShrink:0}} onError={e=>{e.currentTarget.style.display="none";}}/>
                                  <div style={{flex:1}}>
                                    <p style={{fontSize:12,fontWeight:700,color:T.ink}}>{rate.productName??"Seller Shipping"}</p>
                                    <p style={{fontSize:10,color:T.muted}}>{typeof days==="string"?days:"Est. delivery available"}</p>
                                  </div>
                                  <span style={{fontSize:13,fontWeight:800,color:price===0?T.green:T.ink}}>{price===0?"Free":fmtPrice(price,sym)}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    {checkedBrandIds.some(bid=>selectedBrandRates[bid])&&(
                      <div style={{display:"flex",justifyContent:"space-between",padding:"10px 14px",background:T.bgSoft,border:`1.5px solid rgba(106,0,255,.15)`,borderRadius:10,marginBottom:16}}>
                        <span style={{fontSize:12,fontWeight:700,color:T.ink}}>Total Shipping (all sellers)</span>
                        <span style={{fontSize:14,fontWeight:900,color:T.purple}}>{fmtPrice(fbsTotal,sym)}</span>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* FBP / DHL */}
                {selectedFulfillment==="FBP" && selectedCarrier==="dhl" && (
                  <motion.div key="fbp-dhl" initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-6}} transition={{duration:.22}}>
                    <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14,padding:"12px 14px",background:T.bgSoft,border:`1.5px solid rgba(106,0,255,.15)`,borderRadius:10}}>
                      <img src={dhlLogo} alt="DHL" style={{height:26,objectFit:"contain"}} onError={e=>{e.currentTarget.style.display="none";}}/>
                      <div><p style={{fontSize:12,fontWeight:700,color:T.ink}}>DHL Express</p><p style={{fontSize:11,color:T.muted}}>Worldwide tracked delivery to {addr.city||addr.country||"your address"}</p></div>
                    </div>
                    {dhlLoading&&<div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10,padding:"20px 0"}}><span style={{width:15,height:15,border:`2px solid rgba(106,0,255,.2)`,borderTopColor:T.purple,borderRadius:"50%",animation:"pcSpin 1s linear infinite",display:"inline-block"}}/><span style={{fontSize:12,color:T.muted}}>Fetching DHL rates…</span></div>}
                    {dhlError&&!dhlLoading&&(
                      <div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 13px",background:"rgba(204,34,0,.05)",border:"1px solid rgba(204,34,0,.2)",borderRadius:10,marginBottom:12}}>
                        <AlertCircle size={12} color={T.red}/><span style={{fontSize:11,color:T.red,flex:1}}>{dhlError}</span>
                        <button onClick={fetchDhlRates} style={{fontSize:10,color:T.purple,background:"none",border:"none",cursor:"pointer",textDecoration:"underline"}}>Retry</button>
                      </div>
                    )}
                    {!dhlLoading&&!dhlError&&dhlRates.map((r:any,i:number)=>{
                      const isSel=selectedDhlRate?.serviceCode===r.serviceCode&&selectedDhlRate?.productName===r.productName;
                      return (
                        <div key={i} className={`rate-row${isSel?" active":""}`} onClick={()=>onSelectDhlRate(r)}>
                          <div style={{width:15,height:15,borderRadius:"50%",border:`2px solid ${isSel?T.purple:"rgba(106,0,255,.2)"}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                            {isSel&&<div style={{width:6,height:6,borderRadius:"50%",background:T.purple}}/>}
                          </div>
                          <img src={dhlLogo} alt="" style={{height:18,objectFit:"contain",flexShrink:0}} onError={e=>{e.currentTarget.style.display="none";}}/>
                          <div style={{flex:1}}>
                            <p style={{fontSize:12,fontWeight:700,color:T.ink}}>{r.productName??"DHL Express"}</p>
                            {r.deliveryTime&&<p style={{fontSize:10,color:T.muted}}>{r.deliveryTime}</p>}
                          </div>
                          <span style={{fontSize:13,fontWeight:800,color:r.totalPrice===0?T.green:T.ink}}>{r.totalPrice===0?"Free":fmtPrice(r.totalPrice,sym)}</span>
                        </div>
                      );
                    })}
                    {!dhlLoading&&!dhlError&&dhlRates.length===0&&<div style={{textAlign:"center" as const,padding:"14px 0",fontSize:12,color:T.muted}}>No DHL rates for this destination.</div>}
                  </motion.div>
                )}

                {/* FBP / Xafarii */}
                {selectedFulfillment==="FBP" && selectedCarrier==="xafarii" && (
                  <motion.div key="fbp-xafarii" initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-6}} transition={{duration:.22}}>
                    <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14,padding:"12px 14px",background:T.bgSoft,border:`1.5px solid rgba(224,90,0,.2)`,borderRadius:10}}>
                      <img src={safariLogo} alt="Xafarii" style={{height:26,objectFit:"contain"}} onError={e=>{e.currentTarget.style.display="none";}}/>
                      <div><p style={{fontSize:12,fontWeight:700,color:T.ink}}>Xafarii Express</p><p style={{fontSize:11,color:T.muted}}>Fast delivery to {addr.city||addr.country||"your address"}</p></div>
                    </div>
                    {safaLoading&&<div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10,padding:"20px 0"}}><span style={{width:15,height:15,border:"2px solid #EDE8E0",borderTopColor:"#E05A00",borderRadius:"50%",animation:"pcSpin 1s linear infinite",display:"inline-block"}}/><span style={{fontSize:12,color:T.muted}}>Fetching Xafarii rates…</span></div>}
                    {safaError&&!safaLoading&&(
                      <div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 13px",background:"rgba(224,90,0,.05)",border:"1px solid rgba(224,90,0,.2)",borderRadius:10,marginBottom:12}}>
                        <AlertCircle size={12} color="#E05A00"/><span style={{fontSize:11,color:"#E05A00",flex:1}}>{safaError}</span>
                        <button onClick={fetchSafaRates} style={{fontSize:10,color:T.purple,background:"none",border:"none",cursor:"pointer",textDecoration:"underline"}}>Retry</button>
                      </div>
                    )}
                    {!safaLoading&&!safaError&&safaRates.map((r:any,i:number)=>{
                      const isSel=selectedSafaRate?.serviceCode===r.serviceCode&&selectedSafaRate?.productName===r.productName;
                      return (
                        <div key={i} className={`rate-row${isSel?" active":""}`} onClick={()=>onSelectSafaRate(r)}>
                          <div style={{width:15,height:15,borderRadius:"50%",border:`2px solid ${isSel?"#E05A00":"rgba(224,90,0,.2)"}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                            {isSel&&<div style={{width:6,height:6,borderRadius:"50%",background:"#E05A00"}}/>}
                          </div>
                          <img src={safariLogo} alt="" style={{height:18,objectFit:"contain",flexShrink:0}} onError={e=>{e.currentTarget.style.display="none";}}/>
                          <div style={{flex:1}}>
                            <p style={{fontSize:12,fontWeight:700,color:T.ink}}>{r.productName??"Xafarii Express"}</p>
                            {r.deliveryTime&&<p style={{fontSize:10,color:T.muted}}>{r.deliveryTime}</p>}
                          </div>
                          <span style={{fontSize:13,fontWeight:800,color:r.totalPrice===0?T.green:T.ink}}>{r.totalPrice===0?"Free":fmtPrice(r.totalPrice,sym)}</span>
                        </div>
                      );
                    })}
                    {!safaLoading&&!safaError&&safaRates.length===0&&<div style={{textAlign:"center" as const,padding:"14px 0",fontSize:12,color:T.muted}}>No Xafarii rates for this destination.</div>}
                  </motion.div>
                )}

                {/* FBA tier table */}
                {selectedFulfillment==="FBA" && selectedCarrier==="fba" && (
                  <motion.div key="fba-tiers" initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-6}} transition={{duration:.22}}>
                    <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14,padding:"12px 14px",background:"rgba(21,128,61,.04)",border:"1px solid rgba(21,128,61,.2)",borderRadius:10}}>
                      <img src={fbaLogo} alt="FBA" style={{height:26,objectFit:"contain"}} onError={e=>{e.currentTarget.style.display="none";}}/>
                      <div><p style={{fontSize:12,fontWeight:700,color:T.ink}}>Metarelm Fulfilment Center</p><p style={{fontSize:11,color:T.muted}}>Stored &amp; shipped from the Metarelm warehouse</p></div>
                    </div>
                    <div style={{background:"#fff",border:"1px solid rgba(21,128,61,.18)",borderRadius:10,overflow:"hidden",marginBottom:16}}>
                      <div style={{padding:"11px 14px",background:"rgba(21,128,61,.05)",borderBottom:"1px solid rgba(21,128,61,.12)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <span style={{fontSize:11,fontWeight:700,color:T.green,letterSpacing:".06em",textTransform:"uppercase" as const}}>Your FBA Fee</span>
                        <span style={{fontSize:18,fontWeight:900,color:T.green}}>{fmtPrice(fbaFee,sym)}</span>
                      </div>
                      <div style={{padding:"10px 0 4px"}}>
                        <div style={{padding:"4px 14px 8px",fontSize:10,fontWeight:700,color:T.muted,letterSpacing:".08em",textTransform:"uppercase" as const}}>Fee Tiers</div>
                        {FBA_TIERS.map((tier,i)=>{
                          const active=(i===0&&subtotal>5000)||(i===1&&subtotal>2000&&subtotal<=5000)||
                            (i===2&&subtotal>1000&&subtotal<=2000)||(i===3&&subtotal>500&&subtotal<=1000)||
                            (i===4&&subtotal>200&&subtotal<=500)||(i===5&&subtotal<=200);
                          return (
                            <div key={i} className={`fba-tier-row${active?" hi":""}`}>
                              <span style={{color:active?T.green:T.muted}}>{tier.label}</span>
                              <span style={{fontWeight:active?800:500,color:active?T.green:T.ink}}>
                                {tier.pct}{active&&<span style={{fontSize:9,marginLeft:6,color:T.green}}>← your order</span>}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* ── Order total summary ── */}
              <div style={{background:T.bgSoft,border:`1.5px solid rgba(106,0,255,.15)`,borderRadius:12,padding:"13px 15px",marginBottom:20}}>
                <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid rgba(106,0,255,.08)`}}>
                  <span style={{fontSize:12,color:T.muted}}>Items subtotal</span>
                  <span style={{fontSize:12,fontWeight:600}}>{fmtPrice(subtotal,sym)}</span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid rgba(106,0,255,.08)`}}>
                  <span style={{fontSize:12,color:T.muted}}>
                    Delivery
                    <span style={{marginLeft:6,fontSize:9,fontWeight:800,color:selectedFulfillment==="FBA"?T.green:T.purple,background:selectedFulfillment==="FBA"?"rgba(21,128,61,.08)":"rgba(106,0,255,.08)",padding:"1px 5px",borderRadius:3}}>{selectedFulfillment}</span>
                  </span>
                  <span style={{fontSize:12,fontWeight:600}}>{fmtPrice(deliveryFee,sym)}</span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0 2px"}}>
                  <span style={{fontSize:14,fontWeight:700,color:T.ink}}>Total</span>
                  <span style={{fontSize:18,fontWeight:900,background:GRAD,backgroundSize:"200% 100%",animation:"gradSpin 3s ease infinite",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>
                    {fmtPrice(grandTotal+deliveryFee,sym)}
                  </span>
                </div>
              </div>

              {/* ══════════════════════════════════════════
                  PAYMENT METHOD SELECTOR — single section,
                  two cards (Stripe / M-Pesa), one button.
              ══════════════════════════════════════════ */}
              <div style={{marginBottom:14}}>
                <p style={{fontSize:10,fontWeight:700,color:T.muted,letterSpacing:".14em",textTransform:"uppercase" as const,marginBottom:10}}>Choose payment method</p>
                <div className="pay-method-grid">
                  {/* Stripe card */}
                  <div
                    className={`pay-method-card stripe${selectedPayment==="stripe"?" selected":""}`}
                    onClick={()=>onSelectPayment("stripe")}
                  >
                    <div className="pay-method-top-bar" style={{background:"linear-gradient(90deg,#007BFF,#6A00FF,#FF00D4)"}}/>
                    <div className="pay-method-check">
                      {selectedPayment==="stripe"&&<Check size={9} color="#fff"/>}
                    </div>
                    {/* Stripe logo mark — inline SVG so no extra import needed */}
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <rect width="28" height="28" rx="6" fill="#635BFF"/>
                      <path d="M13.007 11.16c0-.78.642-1.08 1.705-1.08 1.525 0 3.452.46 4.976 1.28V7.6C18.17 6.92 16.66 6.6 15.15 6.6c-3.17 0-5.278 1.66-5.278 4.42 0 4.32 5.948 3.62 5.948 5.48 0 .92-.8 1.22-1.926 1.22-1.665 0-3.79-.68-5.474-1.6v3.8c1.865.8 3.75 1.14 5.474 1.14 3.251 0 5.494-1.6 5.494-4.4-.02-4.66-5.98-3.82-5.98-5.5z" fill="#fff"/>
                    </svg>
                    <span className="pay-method-label">Stripe</span>
                    <span className="pay-method-sub">Cards · Apple Pay · Google Pay</span>
                  </div>

                  {/* M-Pesa / Cosmoola card */}
                  <div
                    className={`pay-method-card mpesa${selectedPayment==="mpesa"?" selected":""}`}
                    onClick={()=>onSelectPayment("mpesa")}
                  >
                    <div className="pay-method-top-bar" style={{background:"linear-gradient(90deg,#00A651,#007A3D)"}}/>
                    <div className="pay-method-check">
                      {selectedPayment==="mpesa"&&<Check size={9} color="#fff"/>}
                    </div>
                    <Smartphone size={28} color={selectedPayment==="mpesa"?T.mpesa:T.muted}/>
                    <span className="pay-method-label" style={{color:selectedPayment==="mpesa"?T.mpesa:undefined}}>M-Pesa</span>
                    <span className="pay-method-sub">via Cosmoola · Mobile money</span>
                  </div>
                </div>
              </div>

              {/* ── Single context-aware pay button ── */}
              <AnimatePresence mode="wait">
                {selectedPayment==="stripe" ? (
                  <motion.button key="stripe-btn"
                    initial={{opacity:0,y:6}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-4}}
                    transition={{duration:.18}}
                    whileHover={canPay?{scale:1.01} as any:{}}
                    whileTap={canPay?{scale:.98} as any:{}}
                    onClick={onPayStripe}
                    className={`pay-btn-stripe${canPay?" active":" inactive"}`}
                  >
                    {payingStripe
                      ? <span style={{width:13,height:13,border:"2px solid rgba(255,255,255,.6)",borderTopColor:"transparent",borderRadius:"50%",animation:"pcSpin 1s linear infinite"}}/>
                      : <Lock size={12}/>
                    }
                    {payingStripe ? "Processing…" : `Continue with Stripe  ${fmtPrice(grandTotal+deliveryFee,sym)}`}
                  </motion.button>
                ) : (
                  <motion.button key="mpesa-btn"
                    initial={{opacity:0,y:6}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-4}}
                    transition={{duration:.18}}
                    whileHover={canPay?{scale:1.01} as any:{}}
                    whileTap={canPay?{scale:.98} as any:{}}
                    onClick={onPayMpesa}
                    className={`pay-btn-mpesa${canPay?" active":" inactive"}`}
                  >
                    {payingMpesa
                      ? <span style={{width:13,height:13,border:"2px solid rgba(255,255,255,.6)",borderTopColor:"transparent",borderRadius:"50%",animation:"pcSpin 1s linear infinite"}}/>
                      : <Smartphone size={13}/>
                    }
                    {payingMpesa ? "Connecting to M-Pesa…" : `Continue with M-Pesa  ${fmtPrice(grandTotal+deliveryFee,sym)}`}
                  </motion.button>
                )}
              </AnimatePresence>

              {/* Error */}
              <AnimatePresence>
                {payError&&(
                  <motion.div initial={{opacity:0,y:-6}} animate={{opacity:1,y:0}} exit={{opacity:0}}
                    style={{display:"flex",alignItems:"flex-start",gap:8,background:"rgba(204,34,0,.05)",border:"1px solid rgba(204,34,0,.2)",padding:"11px 13px",marginTop:10,borderRadius:10}}>
                    <AlertCircle size={13} color={T.red} style={{flexShrink:0,marginTop:1}}/>
                    <span style={{fontSize:11,color:T.red,flex:1}}>{payError}</span>
                    <button onClick={onDismissError} style={{background:"none",border:"none",padding:0}}><X size={12} color={T.red}/></button>
                  </motion.div>
                )}
              </AnimatePresence>

              <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:20,marginTop:16}}>
                {[{icon:"🔒",label:"Secure"},{icon:"↩️",label:"Returns"},{icon:"📦",label:"Tracked"}].map(b=>(
                  <div key={b.label} style={{display:"flex",alignItems:"center",gap:5}}>
                    <span style={{fontSize:12}}>{b.icon}</span>
                    <span style={{fontSize:10,color:T.muted,fontWeight:600}}>{b.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const ProductCheckout: React.FC = () => {
  const { userId } = useParams<{ userId:string }>();
  const navigate   = useNavigate();
  const dispatch   = useDispatch<AppDispatch>();

  const items    = useSelector((s:RootState)=>s.cart.items);
  const listings = useSelector((s:RootState)=>s.products.products);
  const fees     = useSelector((s:RootState)=>s.classifiedCommerceServiceFees);
  const user     = useSelector((s:RootState)=>s.auth.user);
  const isLoggedIn = !!user;

  const [isMobile, setIsMobile] = useState(window.innerWidth<=768);
  useEffect(()=>{ const fn=()=>setIsMobile(window.innerWidth<=768); window.addEventListener("resize",fn); return()=>window.removeEventListener("resize",fn); },[]);

  const STEPS = ["cart","delivery","shipping"] as const;
  type StepKey = typeof STEPS[number];
  const [step, setStep] = useState<StepKey>("cart");
  const stepIdx = STEPS.indexOf(step);

  const [addr, setAddr] = useState<AddrState>({
    firstName:"", lastName:"", email:"",
    country:"", state:"", city:"", postalCode:"",
    address:"", dialCode:"+1", phone:"",
  });
  const patchAddr = useCallback((patch: Partial<AddrState>) => setAddr(p=>({...p,...patch})),[]);

  useEffect(()=>{
    if(!user)return;
    const ship=user.shippingAddress;
    const email=user.contactEmail||user.email||user.emailAddress||"";
    const rawPhone=user.contactNumber||user.whatsappNumber||"";
    const dialMatch=rawPhone.match(/^(\+\d{1,4})\s?/);
    const dialCode=dialMatch?dialMatch[1]:(ship?.countryCode?`+${ship.countryCode}`:"+1");
    const phone=dialMatch?rawPhone.replace(dialMatch[0],""):rawPhone;
    setAddr({ firstName:user.firstName||"", lastName:user.lastName||(user as any).surName||"", email, country:ship?.country||"", state:ship?.state||"", city:ship?.city||"", postalCode:ship?.zipCode||"", address:ship?.streetAddress||"", dialCode, phone });
  },[user]);

const [buyForSomeoneElse, setBuyForSomeoneElse] = useState(false);

/* GPS coords for backend delivery zone matching */
const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null);
useEffect(() => {
  navigator.geolocation.getCurrentPosition(
    (pos) => setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
    () => setUserCoords(null),
  );
}, []);


  /* Payment method */
  const [selectedPayment, setSelectedPayment] = useState<PaymentMethod>("stripe");

  /* Brand IDs */
  const checkedBrandIds = useMemo(()=>Array.from(new Set(items.map((i:any)=>i.brandId??i.hostId).filter(Boolean))),[items]);

  /* FBS per-brand rates */
  const [brandDhlRates,setBrandDhlRates]=useState<Record<string,any[]>>({});
  const [brandDhlLoading,setBrandDhlLoading]=useState<Record<string,boolean>>({});
  const [brandDhlErrors,setBrandDhlErrors]=useState<Record<string,string|null>>({});
  const [selectedBrandRates,setSelectedBrandRates]=useState<Record<string,any>>({});

  const fetchBrandDhlRates=useCallback(async()=>{
    if(!addr.country||!addr.postalCode)return;
    const totalQtyLocal=items.reduce((s:number,i:any)=>s+(i.qty||1),0);
    const weightPerBrand=Math.max(0.5,(totalQtyLocal*0.4)/Math.max(1,checkedBrandIds.length));
    for(const bid of checkedBrandIds){
      setBrandDhlLoading(p=>({...p,[bid]:true})); setBrandDhlErrors(p=>({...p,[bid]:null}));
      try{
        const res=await axios.post(DHL_RATES_URL,{ toCountry:addr.country, toPostalCode:addr.postalCode, toCity:addr.city, weight:weightPerBrand, brandId:bid });
        const d=res.data as any; const rates=Array.isArray(d)?d:d?.products??[];
        setBrandDhlRates(p=>({...p,[bid]:rates}));
        if(rates.length>0){ const cheapest=[...rates].sort((a:any,b:any)=>(a.totalPrice??0)-(b.totalPrice??0))[0]; setSelectedBrandRates(p=>({...p,[bid]:cheapest})); }
      }catch{ setBrandDhlErrors(p=>({...p,[bid]:"Could not fetch rates."})); setBrandDhlRates(p=>({...p,[bid]:[]})); }
      setBrandDhlLoading(p=>({...p,[bid]:false}));
    }
  },[addr.country,addr.postalCode,addr.city,checkedBrandIds,items]);

  const onSelectBrandRate=useCallback((brandId:string,rate:any)=>setSelectedBrandRates(p=>({...p,[brandId]:rate})),[]);

  /* DHL rates */
  const [dhlRates,setDhlRates]=useState<any[]>([]);
  const [dhlLoading,setDhlLoading]=useState(false);
  const [dhlError,setDhlError]=useState<string|null>(null);
  const [selectedDhlRate,setSelectedDhlRate]=useState<any|null>(null);

  const fetchDhlRates=useCallback(async()=>{
    if(!addr.country||!addr.postalCode)return;
    setDhlLoading(true); setDhlError(null);
    try{
      const res=await axios.post(DHL_RATES_URL,{ toCountry:addr.country, toPostalCode:addr.postalCode, toCity:addr.city, weight:Math.max(0.5,items.reduce((s:number,i:any)=>s+(i.qty||1),0)*0.4), network:"FBP" });
      const d=res.data as any; const rates=Array.isArray(d)?d:d?.products??[];
      setDhlRates(rates);
      if(rates.length>0){ const cheapest=[...rates].sort((a:any,b:any)=>(a.totalPrice??0)-(b.totalPrice??0))[0]; setSelectedDhlRate(cheapest); }
    }catch{ setDhlError("Could not fetch DHL rates. Please try again."); }
    setDhlLoading(false);
  },[addr.country,addr.postalCode,addr.city,items]);

  /* Xafarii rates */
  const [safaRates,setSafaRates]=useState<any[]>([]);
  const [safaLoading,setSafaLoading]=useState(false);
  const [safaError,setSafaError]=useState<string|null>(null);
  const [selectedSafaRate,setSelectedSafaRate]=useState<any|null>(null);

  const fetchSafaRates=useCallback(async()=>{
    if(!addr.country||!addr.postalCode)return;
    setSafaLoading(true); setSafaError(null);
    try{
      const res=await axios.post(XAFARII_RATES_URL,{ toCountry:addr.country, toPostalCode:addr.postalCode, toCity:addr.city, weight:Math.max(0.5,items.reduce((s:number,i:any)=>s+(i.qty||1),0)*0.4) });
      const d=res.data as any; const rates=Array.isArray(d)?d:d?.products??[];
      setSafaRates(rates);
      if(rates.length>0){ const cheapest=[...rates].sort((a:any,b:any)=>(a.totalPrice??0)-(b.totalPrice??0))[0]; setSelectedSafaRate(cheapest); }
    }catch{ setSafaError("Could not fetch Xafarii rates. Please try again."); }
    setSafaLoading(false);
  },[addr.country,addr.postalCode,addr.city,items]);

  /* Fulfillment + carrier */
  const [selectedFulfillment,setSelectedFulfillment]=useState<"FBS"|"FBP"|"FBA">("FBP");
  const [selectedCarrier,setSelectedCarrier]=useState<CarrierKey>("dhl");

  const handleSelectCarrier=useCallback((c:CarrierKey)=>{
    setSelectedCarrier(c);
    if(c==="dhl")     { setSelectedFulfillment("FBP"); fetchDhlRates(); }
    if(c==="xafarii") { setSelectedFulfillment("FBP"); fetchSafaRates(); }
    if(c==="fba")     { setSelectedFulfillment("FBA"); }
    if(c==="fbs")     { setSelectedFulfillment("FBS"); fetchBrandDhlRates(); }
  },[fetchDhlRates,fetchSafaRates,fetchBrandDhlRates]);

  const { subtotal, totalQty, guestsFee, hostProfits, platformProfit, totalCharged } = fees;

  const fbsTotal      = checkedBrandIds.reduce((sum,bid)=>{ const r=selectedBrandRates[bid]; return sum+(r?(r.totalPrice??r.price??0):0); },0);
  const dhlTotalMain  = selectedDhlRate  ? (selectedDhlRate.totalPrice  ??0):0;
  const safaTotalMain = selectedSafaRate ? (selectedSafaRate.totalPrice ??0):0;
  const fbpTotal      = selectedCarrier==="dhl" ? dhlTotalMain : safaTotalMain;
  const fbaFee        = calculateFbaFee(subtotal||0);
  const deliveryFee   = selectedFulfillment==="FBS" ? fbsTotal : selectedFulfillment==="FBP" ? fbpTotal : fbaFee;
  const grandTotal    = (totalCharged||0)+deliveryFee;

  const [payingStripe, setPayingStripe] = useState(false);
  const [payingMpesa,  setPayingMpesa]  = useState(false);
  const [payError, setPayError] = useState<string|null>(null);
  const paying = payingStripe || payingMpesa;
  const canPay = !paying && items.length>0 && step==="shipping";

  useEffect(()=>{ const ids=Array.from(new Set(items.map((i:any)=>i.listingId))); ids.forEach((id:any)=>dispatch(listenToProductData(id))); },[items,dispatch]);
  useEffect(()=>{ if(!items.length)return; dispatch(calculateClassifiedEarnings({items,deliveryByBrand:{}})); },[items,dispatch]);
  useEffect(()=>{ if(!items.length)navigate("/ProductHome"); },[items,navigate]);

  const handleContinueDelivery=useCallback((latest:AddrState)=>{
    setAddr(latest); setStep("shipping"); fetchDhlRates();
  },[fetchDhlRates]);

  const ensureCustomerAccount=async():Promise<string>=>{
    if(isLoggedIn&&!buyForSomeoneElse)return user!.userId;
    const res=await axios.post<CreateCustomerResponse>(CREATE_CUSTOMER_URL,{
      firstName:addr.firstName, lastName:addr.lastName, email:addr.email,
      phone:`${addr.dialCode}${addr.phone}`,
      shippingAddress:{ streetAddress:addr.address, city:addr.city, state:addr.state, country:addr.country, zipCode:addr.postalCode },
      buyForSomeoneElse, buyerUserId:isLoggedIn?user!.userId:null,
    });
    if(!res.data?.userId)throw new Error("Account creation failed — no userId returned.");
    return res.data.userId;
  };

  
/* Shared payload builder — shape matches backend exactly */
  const buildPayload = useCallback((resolvedUserId: string) => {
    // Backend expects "listingId:hostId:colorId:sizeId:qty"
    const itemRefs = items.map((item: any) =>
      [
        item.listingId,
        item.hostId ?? item.brandId ?? "unknown",
        item.colorId ?? "n",
        item.sizeId  ?? "n",
        item.qty,
      ].join(":")
    );

    return {
      userId:        resolvedUserId,
      itemRefs,
      userLatitude:  userCoords?.lat ?? 0,
      userLongitude: userCoords?.lng ?? 0,
      // Extra metadata stored in Stripe session + used by Cosmoola
      fulfillmentMethod: selectedFulfillment,
      carrier:           selectedCarrier,
      paymentMethod:     selectedPayment,
      buyForSomeoneElse,
      buyerUserId:       user?.userId ?? null,
      shippingAddress: {
        firstName:   addr.firstName,
        lastName:    addr.lastName,
        email:       addr.email,
        phone:       `${addr.dialCode}${addr.phone}`,
        address:     addr.address,
        city:        addr.city,
        state:       addr.state,
        country:     addr.country,
        postalCode:  addr.postalCode,
      },
    };
  }, [items, userCoords, selectedFulfillment, selectedCarrier, selectedPayment,
      buyForSomeoneElse, user, addr]);


  /* ── Pay with Stripe ── */
  const handlePayStripe = useCallback(async () => {
    if (!canPay) return;
    setPayingStripe(true); setPayError(null);
    try {
      const resolvedUserId = await ensureCustomerAccount();
      // Backend returns { id: session.id } — redirect via Stripe.js
      const res = await axios.post<{ id: string }>(STRIPE_CHECKOUT_URL, buildPayload(resolvedUserId));
      const sessionId = res.data?.id;
      if (!sessionId) throw new Error(`No session ID returned: ${JSON.stringify(res.data)}`);
      // Redirect to Stripe hosted checkout
      const { loadStripe } = await import("@stripe/stripe-js");
      const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PK!);
      const { error } = await stripe!.redirectToCheckout({ sessionId });
      if (error) throw new Error(error.message);
    } catch (e: any) {
      setPayError(e?.message ?? "Stripe payment failed. Please try again.");
      setPayingStripe(false);
    }
  }, [canPay, buildPayload]);

  /* ── Pay with M-Pesa via Cosmoola ── */
  const handlePayMpesa = useCallback(async () => {
    if (!canPay) return;
    setPayingMpesa(true); setPayError(null);
    try {
      const resolvedUserId = await ensureCustomerAccount();
      const res = await axios.post<CheckoutResponse>(COSMOOLA_CHECKOUT_URL, buildPayload(resolvedUserId));
      const url = res.data?.url ?? res.data?.data?.url ?? res.data?.sessionUrl ?? res.data?.checkoutUrl;
      if (!url) throw new Error(`No Cosmoola URL returned: ${JSON.stringify(res.data)}`);
      window.location.href = url;
    } catch (e: any) {
      setPayError(e?.message ?? "M-Pesa payment failed. Please try again.");
      setPayingMpesa(false);
    }
  }, [canPay, buildPayload]);

  

  const handleInc    = useCallback((item:any)=>dispatch(addToCart({listingId:item.listingId,name:item.name,hostId:item.hostId,price:item.price,colorId:item.colorId,sizeId:item.sizeId,qty:1,brandId:item.brandId})),[dispatch]);
  const handleDec    = useCallback((item:any)=>dispatch(decrementCartItem({listingId:item.listingId,colorId:item.colorId,sizeId:item.sizeId})),[dispatch]);
  const handleRemove = useCallback((item:any)=>dispatch(removeFromCart({listingId:item.listingId,colorId:item.colorId,sizeId:item.sizeId})),[dispatch]);
  const handleNav    = useCallback((id:string)=>navigate(`/ProductDetails/${id}`),[navigate]);

  const sym = "€";

  return (
    <div style={{ minHeight:"100vh", background:T.bg, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <style>{CSS}</style>

      {/* Header */}
      <header style={{ position:"sticky", top:0, zIndex:300, background:"#fff", borderBottom:`1px solid rgba(106,0,255,.12)`, boxShadow:"0 1px 12px rgba(106,0,255,.06)" }}>
        <div style={{ height:3, background:GRAD }}/>
        <div style={{ background:"rgba(106,0,255,.05)", textAlign:"center", fontSize:9, padding:"4px 0", letterSpacing:".2em", fontWeight:800, color:T.purple, borderBottom:`1px solid rgba(106,0,255,.08)` }}>
          SECURE CHECKOUT · SSL ENCRYPTED · STRIPE & M-PESA
        </div>
        <div style={{ display:"flex", alignItems:"center", padding:"0 20px", height:52, gap:14 }}>
          <button onClick={()=>navigate(-1)} style={{ background:"none", border:"none", color:T.muted, display:"flex", alignItems:"center", gap:5, fontSize:11, letterSpacing:".1em", textTransform:"uppercase" as const }}>
            <ArrowLeft size={14}/> Back
          </button>
          <div onClick={()=>navigate(`/ProductsHome/${userId}`)} style={{ position:"absolute", left:"50%", transform:"translateX(-50%)", cursor:"pointer", display:"flex", alignItems:"center", gap:8 }}>
            <img src={logo} alt="" style={{ width:26, height:26, objectFit:"cover", borderRadius:"50%" }} onError={e=>(e.currentTarget.style.display="none")}/>
            <span style={{ fontSize:14, fontWeight:900, letterSpacing:1.5, color:T.ink }}>METARELM</span>
          </div>
          <div style={{ flex:1 }}/>
          <div style={{ display:"flex", alignItems:"center", gap:5 }}>
            <ShoppingBag size={15} color={T.muted}/>
            <span style={{ fontSize:12, color:T.ink, fontWeight:800 }}>{totalQty}</span>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:"8px 0 10px", borderTop:`1px solid rgba(106,0,255,.08)` }}>
          {STEPS.map((s,i)=>(
            <React.Fragment key={s}>
              <span className={`bc-step${s===step?" active":""}`}>{s}</span>
              {i<STEPS.length-1&&<span className="bc-sep">›</span>}
            </React.Fragment>
          ))}
        </div>
      </header>

      {/* Body */}
      <div style={{ maxWidth:1080, margin:"0 auto", padding:isMobile?"16px 14px 120px":"28px 40px 60px", display:"grid", gridTemplateColumns:isMobile?"1fr":"minmax(0,1fr) 340px", gap:28, alignItems:"start" }}>

        {/* Left — steps */}
        <div style={{ minWidth:0 }}>
          <CartStep
            items={items} listings={listings as any[]} stepIdx={stepIdx} sym={sym}
            onContinue={()=>setStep("delivery")}
            onInc={handleInc} onDec={handleDec} onRemove={handleRemove} onNavigate={handleNav}
          />
          {stepIdx>=1&&(
            <DeliveryStep
              stepIdx={stepIdx} isLoggedIn={isLoggedIn} initialAddr={addr}
              buyForSomeoneElse={buyForSomeoneElse} onToggleBuyForSomeoneElse={()=>setBuyForSomeoneElse(v=>!v)}
              onFlush={patchAddr} onContinue={handleContinueDelivery} onBack={()=>setStep("delivery")}
            />
          )}
          {stepIdx>=2&&(
            <ShippingStep
              stepIdx={stepIdx} addr={addr}
              subtotal={subtotal||0} totalQty={totalQty||0} sym={sym}
              grandTotal={totalCharged||0}
              canPay={canPay} payingStripe={payingStripe} payingMpesa={payingMpesa}
              payError={payError} onDismissError={()=>setPayError(null)}
              onPayStripe={handlePayStripe} onPayMpesa={handlePayMpesa}
              selectedPayment={selectedPayment} onSelectPayment={setSelectedPayment}
              checkedBrandIds={checkedBrandIds}
              brandDhlRates={brandDhlRates} brandDhlLoading={brandDhlLoading}
              brandDhlErrors={brandDhlErrors} selectedBrandRates={selectedBrandRates}
              onSelectBrandRate={onSelectBrandRate} fetchBrandDhlRates={fetchBrandDhlRates}
              selectedFulfillment={selectedFulfillment} onSelectFulfillment={setSelectedFulfillment}
              selectedCarrier={selectedCarrier} onSelectCarrier={handleSelectCarrier}
              dhlRates={dhlRates} dhlLoading={dhlLoading} dhlError={dhlError}
              selectedDhlRate={selectedDhlRate} onSelectDhlRate={setSelectedDhlRate} fetchDhlRates={fetchDhlRates}
              safaRates={safaRates} safaLoading={safaLoading} safaError={safaError}
              selectedSafaRate={selectedSafaRate} onSelectSafaRate={setSelectedSafaRate} fetchSafaRates={fetchSafaRates}
            />
          )}
        </div>

        {/* Right — Order Summary */}
        <div style={{ position:isMobile?"static":"sticky", top:116, minWidth:0 }}>
          <div style={{ background:"#fff", border:`1.5px solid rgba(106,0,255,.15)`, padding:"20px 18px", marginBottom:12, borderRadius:16 }}>
            <div style={{ fontSize:9, fontWeight:800, letterSpacing:".22em", color:T.muted, marginBottom:16 }}>ORDER SUMMARY</div>
            <FeeRow label="Subtotal"    value={fmtPrice(subtotal||0,sym)} sub={`${totalQty} item${totalQty!==1?"s":""}`}/>
            <FeeRow label="Service Fee" value={fmtPrice(guestsFee||0,sym)} sub="Platform fee" muted/>
            <FeeRow label="Delivery"
              value={selectedFulfillment==="FBA"
                ? fmtPrice(fbaFee,sym)
                : selectedFulfillment==="FBP"
                  ? (selectedCarrier==="dhl"
                      ? (selectedDhlRate  ? fmtPrice(dhlTotalMain, sym) : "Select rate")
                      : (selectedSafaRate ? fmtPrice(safaTotalMain,sym) : "Select rate"))
                  : (checkedBrandIds.some(bid=>selectedBrandRates[bid]) ? fmtPrice(fbsTotal,sym) : "Select rates")}
              loading={Object.values(brandDhlLoading).some(Boolean)||dhlLoading||safaLoading}
            />
            {buyForSomeoneElse&&<FeeRow label="Gift Order" value="Account created for recipient" green/>}
            <div style={{ paddingTop:14, display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
              <span style={{ fontSize:11, fontWeight:800, letterSpacing:".15em", color:T.ink }}>TOTAL</span>
              <span style={{ fontSize:28, fontWeight:900, background:GRAD, backgroundSize:"200% 100%", animation:"gradSpin 3s ease infinite", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>{fmtPrice(grandTotal,sym)}</span>
            </div>
            <div style={{ fontSize:10, color:T.muted, textAlign:"right", marginTop:4 }}>All taxes included · Charged in EUR</div>
          </div>

          {/* Payment method indicator in sidebar */}
          <div style={{ display:"flex", alignItems:"center", gap:10, padding:"11px 14px", marginBottom:12,
            border:`1.5px solid ${selectedPayment==="mpesa"?"rgba(0,166,81,.3)":"rgba(106,0,255,.2)"}`,
            borderRadius:12, background:selectedPayment==="mpesa"?"rgba(0,166,81,.04)":"rgba(106,0,255,.04)",
            transition:"all .2s" }}>
            {selectedPayment==="stripe"
              ? <CreditCard size={14} color={T.purple}/>
              : <Smartphone size={14} color={T.mpesa}/>
            }
            <div>
              <p style={{ fontSize:11, fontWeight:700, color:selectedPayment==="mpesa"?T.mpesa:T.purple, letterSpacing:".04em" }}>
                {selectedPayment==="stripe" ? "Paying via Stripe" : "Paying via M-Pesa"}
              </p>
              <p style={{ fontSize:10, color:T.muted, marginTop:2 }}>
                {selectedPayment==="stripe" ? "Cards · Apple Pay · Google Pay" : "Cosmoola · Mobile money"}
              </p>
            </div>
            <Check size={13} color={selectedPayment==="mpesa"?T.mpesa:T.purple} style={{ marginLeft:"auto", flexShrink:0 }}/>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:12 }}>
            {[
              { icon:<ShieldCheck size={14} color={T.purple}/>, label:"Encrypted" },
              { icon:<CreditCard  size={14} color={T.purple}/>, label:"Stripe" },
              { icon:<RotateCcw   size={14} color={T.purple}/>, label:"Refunds" },
            ].map(b=>(
              <div key={b.label} style={{ border:`1.5px solid rgba(106,0,255,.12)`, padding:"10px 8px", display:"flex", flexDirection:"column", alignItems:"center", gap:5, background:T.bgSoft, borderRadius:12 }}>
                {b.icon}
                <span style={{ fontSize:9, fontWeight:800, color:T.muted, letterSpacing:".1em" }}>{b.label.toUpperCase()}</span>
              </div>
            ))}
          </div>

          <div style={{ marginBottom:12 }}>
            <img src={checkoutPay} alt="Accepted payment methods" style={{ width:"100%", display:"block", borderRadius:10, border:`1px solid rgba(106,0,255,.08)` }} onError={e=>{e.currentTarget.style.display="none";}}/>
          </div>

          <div style={{ border:`1.5px solid rgba(106,0,255,.12)`, overflow:"hidden", background:"#fff", borderRadius:12 }}>
            {[
              { icon:<Gift        size={13} color={T.gold}/>,   text:"Complimentary gift wrapping available" },
              { icon:<ShieldCheck size={13} color={T.purple}/>, text:"Every product verified by Metarelm" },
              { icon:<RotateCcw   size={13} color={T.purple}/>, text:"Free returns within 30 days of delivery" },
            ].map((b,i)=>(
              <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"11px 14px", borderTop:i>0?`1px solid rgba(106,0,255,.08)`:"none" }}>
                <span style={{ marginTop:1, flexShrink:0 }}>{b.icon}</span>
                <span style={{ fontSize:11, color:T.muted, lineHeight:1.6 }}>{b.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile Sticky CTA */}
      {isMobile&&(
        <div style={{ position:"fixed", bottom:0, left:0, right:0, background:"rgba(255,255,255,0.97)", borderTop:`1px solid rgba(106,0,255,.12)`, padding:"12px 16px", zIndex:200, backdropFilter:"blur(10px)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
            <span style={{ fontSize:11, color:T.muted }}>Total</span>
            <span style={{ fontSize:20, fontWeight:900, color:T.ink }}>{fmtPrice(grandTotal,sym)}</span>
          </div>
          {step==="cart"&&<button onClick={()=>setStep("delivery")} className="pay-btn-stripe active">Continue to Delivery <ChevronRight size={14}/></button>}
          {step==="delivery"&&<button onClick={()=>{ if(addr.firstName&&addr.phone)handleContinueDelivery(addr); }} className={`pay-btn-stripe${addr.firstName&&addr.phone?" active":" inactive"}`}>Continue to Shipping <ChevronRight size={14}/></button>}
          {step==="shipping"&&(
            selectedPayment==="stripe" ? (
              <motion.button whileTap={canPay?{scale:.98} as any:{}} onClick={handlePayStripe} className={`pay-btn-stripe${canPay?" active":" inactive"}`}>
                {payingStripe?<span style={{width:13,height:13,border:"2px solid rgba(255,255,255,.6)",borderTopColor:"transparent",borderRadius:"50%",animation:"pcSpin 1s linear infinite"}}/>:<Lock size={12}/>}
                {payingStripe?"Processing…":`Continue with Stripe  ${fmtPrice(grandTotal,sym)}`}
              </motion.button>
            ) : (
              <motion.button whileTap={canPay?{scale:.98} as any:{}} onClick={handlePayMpesa} className={`pay-btn-mpesa${canPay?" active":" inactive"}`}>
                {payingMpesa?<span style={{width:13,height:13,border:"2px solid rgba(255,255,255,.6)",borderTopColor:"transparent",borderRadius:"50%",animation:"pcSpin 1s linear infinite"}}/>:<Smartphone size={12}/>}
                {payingMpesa?"Connecting…":`Continue with M-Pesa  ${fmtPrice(grandTotal,sym)}`}
              </motion.button>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default ProductCheckout;
